import React, { useMemo, useState } from 'react'
import { Dropdown, Spin, Tooltip } from 'antd'
import {
  CaretDown,
  CaretLeft,
  CaretRight,
  Info,
} from '@phosphor-icons/react'
import ReactApexChart from 'react-apexcharts'
import {
  useGetLeaguePlayers,
  LeaguePlayerParticipant,
} from 'api/league/LeagueManage/leagueManage'
import { useGetLeagueStandings } from 'api/league/LeagueStanding/LeagueStanding'
import { useGetLeagueAnalytics } from 'api/league/LeagueAnalytics/leagueAnalytics'
import { leagueEventStore } from 'state/league/leagueEvent'
import type { MenuProps } from 'antd'
import '../../styles/analyticstab.scss'

/* ── helpers ──────────────────────────────────────────── */
const getWeekStart = (d: Date): Date => {
  const day = d.getDay()
  const start = new Date(d)
  start.setDate(d.getDate() - ((day + 6) % 7))
  start.setHours(0, 0, 0, 0)
  return start
}
const fmtShort = (d: Date) =>
  d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })
const formatWeekRange = (monday: Date): string => {
  const sunday = new Date(monday)
  sunday.setDate(monday.getDate() + 6)
  return `${fmtShort(monday)} - ${fmtShort(sunday)}`
}

const computeAge = (dob: string): number => {
  const today = new Date()
  const birth = new Date(dob)
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
  return age
}

/* ── static chart options (non-data-dependent) ────────── */
const ageBarOpts: ApexCharts.ApexOptions = {
  chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Montserrat, sans-serif' },
  colors: ['#2ba7ca'],
  dataLabels: { enabled: false },
  xaxis: {
    categories: Array.from({ length: 30 }, (_, i) => String(i + 1)),
    labels: { style: { fontSize: '11px', colors: '#939598' } },
    axisBorder: { show: false },
    axisTicks: { show: false },
  },
  yaxis: {
    labels: { style: { fontSize: '11px', colors: '#939598' } },
    tickAmount: 5,
  },
  grid: { borderColor: '#ebedf1', strokeDashArray: 3 },
  plotOptions: { bar: { borderRadius: 2, columnWidth: '60%' } },
  tooltip: { y: { formatter: (val: number) => `${val} participants` } },
}

const genderDonutOpts: ApexCharts.ApexOptions = {
  chart: { type: 'donut', sparkline: { enabled: true } },
  colors: ['#81a9e4', '#2459a8'],
  dataLabels: { enabled: false },
  legend: { show: false },
  plotOptions: { pie: { donut: { size: '65%', labels: { show: false } } } },
  stroke: { width: 0 },
  tooltip: { y: { formatter: (val: number) => `${val}%` } },
}

/* ── Component ─────────────────────────────────────────── */
const AnalyticsTab: React.FC = () => {
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))
  const leagueId = leagueData?._id || ''

  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(new Date()))
  const [ageGender, setAgeGender] = useState<string>('All')
  const [weekdayPeriod, setWeekdayPeriod] = useState<string>('Week')
  const [hourlyPeriod, setHourlyPeriod] = useState<string>('Week')

  const goWeek = (delta: number) => {
    const next = new Date(weekStart)
    next.setDate(weekStart.getDate() + delta * 7)
    setWeekStart(next)
  }

  /* ── Real data ── */
  const { data: playersData, isLoading: playersLoading } = useGetLeaguePlayers(
    leagueId!,
  )
  const { data: standingsData, isLoading: standingsLoading } =
    useGetLeagueStandings(leagueId!)
  const { data: analyticsData, isLoading: analyticsLoading } =
    useGetLeagueAnalytics({ leagueId: leagueId! })

  const allPlayers: LeaguePlayerParticipant[] = useMemo(() => {
    if (!playersData) return []
    const { kids = [], guardians = [], authorizedAdults = [] } =
      playersData.participants || {}
    return [...kids, ...guardians, ...authorizedAdults]
  }, [playersData])

  const totalParticipants = allPlayers.length

  /* Gender breakdown */
  const genderCounts = useMemo(() => {
    let female = 0
    let male = 0
    allPlayers.forEach(p => {
      if (p.gender === 'female') female++
      else if (p.gender === 'male') male++
    })
    const total = female + male || 1
    return {
      femalePercent: Math.round((female / total) * 100),
      malePercent: Math.round((male / total) * 100),
      female,
      male,
    }
  }, [allPlayers])

  /* Age distribution (age 1-30) */
  const ageData = useMemo(() => {
    const filtered =
      ageGender === 'All'
        ? allPlayers
        : allPlayers.filter(p => p.gender === ageGender.toLowerCase())
    const counts = new Array(30).fill(0)
    filtered.forEach(p => {
      if (p.dob) {
        const age = computeAge(p.dob)
        if (age >= 1 && age <= 30) counts[age - 1]++
      }
    })
    return counts
  }, [allPlayers, ageGender])

  /* Standings table */
  const standingRows = useMemo(() => {
    if (!standingsData?.divisions) return []
    return standingsData.divisions.flatMap(div =>
      div.leagueTeams.map(team => ({
        team: team.name,
        division: div.division.name,
        played: team.played,
        wins: team.wins,
        draws: team.draws,
        losses: team.losses,
        points: team.points,
      })),
    )
  }, [standingsData])

  const moreItems: MenuProps['items'] = [
    { key: 'export', label: 'Export analytics' },
    { key: 'print', label: 'Print report' },
  ]
  const weekItems: MenuProps['items'] = [
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
  ]
  const genderItems: MenuProps['items'] = [
    { key: 'All', label: 'All' },
    { key: 'Male', label: 'Male' },
    { key: 'Female', label: 'Female' },
  ]

  return (
    <div className="an-container">

      {/* ── Section 1: Overview ─────────────────────────── */}
      <div className="an-section">
        <div className="an-section-header">
          <span className="an-section-title">Overview</span>
          <Dropdown
            menu={{ items: moreItems }}
            trigger={['click']}
            placement="bottomRight"
          >
            <button className="an-more-btn">
              More <CaretDown size={12} />
            </button>
          </Dropdown>
        </div>

        <div className="an-controls-row">
          <Dropdown menu={{ items: weekItems }} trigger={['click']}>
            <button className="an-pill-btn">
              Week <CaretDown size={12} />
            </button>
          </Dropdown>
          <div className="an-date-pill">
            <button className="an-date-nav" onClick={() => goWeek(-1)}>
              <CaretLeft size={16} />
            </button>
            <span className="an-date-label">{formatWeekRange(weekStart)}</span>
            <button className="an-date-nav" onClick={() => goWeek(1)}>
              <CaretRight size={16} />
            </button>
          </div>
        </div>

        <div className="an-stat-cards">
          <div className="an-stat-card">
            <div className="an-stat-top">
              <span className="an-stat-name">Participants</span>
              <Tooltip title="Total registered participants">
                <Info size={16} color="#939598" />
              </Tooltip>
            </div>
            <div className="an-stat-bottom">
              {playersLoading ? (
                <Spin size="small" />
              ) : (
                <span className="an-stat-value">{totalParticipants}</span>
              )}
            </div>
          </div>

          <div className="an-stat-card">
            <div className="an-stat-top">
              <span className="an-stat-name">Teams</span>
              <Tooltip title="Total teams across all divisions">
                <Info size={16} color="#939598" />
              </Tooltip>
            </div>
            <div className="an-stat-bottom">
              {standingsLoading ? (
                <Spin size="small" />
              ) : (
                <span className="an-stat-value">{standingRows.length}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Section 2: Gender breakdown ────────────────── */}
      <div className="an-section an-donut-section">
        <div className="an-donut-card">
          <div className="an-donut-header">
            <div className="an-donut-title-row">
              <span className="an-donut-title">Participants gender</span>
              <Tooltip title="Gender breakdown of participants">
                <Info size={16} color="#939598" />
              </Tooltip>
            </div>
          </div>
          {playersLoading ? (
            <div style={{ padding: 24, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : (
            <div className="an-donut-body">
              <div className="an-donut-legend-list an-gender-legend">
                <div className="an-legend-row">
                  <div className="an-legend-left">
                    <span className="an-legend-dot" style={{ background: '#81a9e4' }} />
                    <span className="an-legend-label">
                      Female ({genderCounts.female})
                    </span>
                  </div>
                </div>
                <div className="an-legend-row">
                  <div className="an-legend-left">
                    <span className="an-legend-dot" style={{ background: '#2459a8' }} />
                    <span className="an-legend-label">
                      Male ({genderCounts.male})
                    </span>
                  </div>
                </div>
              </div>
              <div className="an-donut-chart">
                <ReactApexChart
                  type="donut"
                  series={[genderCounts.femalePercent, genderCounts.malePercent]}
                  options={genderDonutOpts}
                  width={164}
                  height={164}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Section 4: Age overview ─────────────────────── */}
      <div className="an-section">
        <div className="an-section-header">
          <div>
            <span className="an-section-title">Age overview</span>
            <div className="an-section-sub">
              <span className="an-section-desc">
                Distribution of participants by age
              </span>
              <Tooltip title="Age distribution data">
                <Info size={14} color="#939598" />
              </Tooltip>
            </div>
          </div>
          <Dropdown
            menu={{
              items: genderItems,
              onClick: ({ key }) => setAgeGender(key),
            }}
            trigger={['click']}
          >
            <button className="an-pill-btn">
              {ageGender} <CaretDown size={12} />
            </button>
          </Dropdown>
        </div>
        {playersLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : (
          <ReactApexChart
            type="bar"
            series={[{ name: 'Participants', data: ageData }]}
            options={ageBarOpts}
            height={280}
          />
        )}
      </div>

      {/* ── Section 5: Revenue vs Expenses ─────────────── */}
      <div className="an-section">
        <div className="an-section-header">
          <div>
            <span className="an-section-title">Revenue vs Expenses</span>
            <div className="an-section-sub">
              <span className="an-section-desc">
                Weekly comparison of income and expenses
              </span>
            </div>
          </div>
          <Dropdown menu={{ items: weekItems }} trigger={['click']}>
            <button className="an-pill-btn">
              Week <CaretDown size={12} />
            </button>
          </Dropdown>
        </div>
        {analyticsLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
        ) : analyticsData?.revenue.byWeek.length ? (
          <>
            <div className="an-stat-cards" style={{ marginBottom: 16 }}>
              <div className="an-stat-card">
                <div className="an-stat-top">
                  <span className="an-stat-name">Total Revenue</span>
                </div>
                <div className="an-stat-bottom">
                  <span className="an-stat-value">
                    ${analyticsData.revenue.totalRevenue.toLocaleString()}
                  </span>
                </div>
              </div>
              <div className="an-stat-card">
                <div className="an-stat-top">
                  <span className="an-stat-name">Est. Expenses</span>
                </div>
                <div className="an-stat-bottom">
                  <span className="an-stat-value">
                    ${analyticsData.revenue.totalExpenses.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
            <ReactApexChart
              type="bar"
              series={[
                {
                  name: 'Revenue',
                  data: analyticsData.revenue.byWeek.map(w => w.revenue),
                },
                {
                  name: 'Expenses',
                  data: analyticsData.revenue.byWeek.map(w => w.expenses),
                },
              ]}
              options={{
                chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Montserrat, sans-serif' },
                colors: ['#E69741', '#2ba7ca'],
                dataLabels: { enabled: false },
                xaxis: {
                  categories: analyticsData.revenue.byWeek.map(w => {
                    const d = new Date(w.weekStart)
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                  }),
                  labels: { style: { fontSize: '11px', colors: '#939598' } },
                  axisBorder: { show: false },
                  axisTicks: { show: false },
                },
                yaxis: {
                  labels: {
                    style: { fontSize: '11px', colors: '#939598' },
                    formatter: (val: number) => `$${val}`,
                  },
                },
                grid: { borderColor: '#ebedf1', strokeDashArray: 3 },
                plotOptions: { bar: { borderRadius: 2, columnWidth: '60%' } },
                legend: { position: 'top' },
                tooltip: { y: { formatter: (val: number) => `$${val.toLocaleString()}` } },
              }}
              height={280}
            />
          </>
        ) : (
          <p className="an-footnote" style={{ padding: '24px 0', textAlign: 'center' }}>
            No registration data yet
          </p>
        )}
      </div>

      {/* ── Section 6: Average purchase rate per weekday ── */}
      <div className="an-section">
        <div className="an-section-header">
          <div>
            <span className="an-section-title">
              Average ticket purchase rate per weekday
            </span>
          </div>
          <Dropdown
            menu={{
              items: weekItems,
              onClick: ({ key }) => setWeekdayPeriod(key),
            }}
            trigger={['click']}
          >
            <button className="an-pill-btn">
              {weekdayPeriod} <CaretDown size={12} />
            </button>
          </Dropdown>
        </div>
        {analyticsLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
        ) : (
          <ReactApexChart
            type="bar"
            series={[{
              name: 'Avg registrations',
              data: (analyticsData?.weekdayPurchaseRate ?? []).map(d => d.avgPerWeek),
            }]}
            options={{
              chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Montserrat, sans-serif' },
              colors: ['#E69741'],
              dataLabels: { enabled: false },
              xaxis: {
                categories: (analyticsData?.weekdayPurchaseRate ?? []).map(d => d.day.slice(0, 3)),
                labels: { style: { fontSize: '11px', colors: '#939598' } },
                axisBorder: { show: false },
                axisTicks: { show: false },
              },
              yaxis: {
                labels: { style: { fontSize: '11px', colors: '#939598' } },
                tickAmount: 4,
              },
              grid: { borderColor: '#ebedf1', strokeDashArray: 3 },
              plotOptions: { bar: { borderRadius: 2, columnWidth: '55%' } },
              tooltip: { y: { formatter: (val: number) => `${val} avg` } },
            }}
            height={240}
          />
        )}
      </div>

      {/* ── Section 7: Hourly purchase rate ──────────────── */}
      <div className="an-section">
        <div className="an-section-header">
          <div>
            <span className="an-section-title">
              Average purchase rate per hour
            </span>
            <div className="an-section-sub">
              <span className="an-section-desc">
                Purchase activity by hour of day
              </span>
            </div>
          </div>
          <Dropdown
            menu={{
              items: weekItems,
              onClick: ({ key }) => setHourlyPeriod(key),
            }}
            trigger={['click']}
          >
            <button className="an-pill-btn">
              {hourlyPeriod} <CaretDown size={12} />
            </button>
          </Dropdown>
        </div>
        {analyticsLoading ? (
          <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
        ) : (
          <ReactApexChart
            type="area"
            series={[{
              name: 'Registrations',
              data: (analyticsData?.hourlyPurchaseRate ?? []).map(h => h.count),
            }]}
            options={{
              chart: { type: 'area', toolbar: { show: false }, fontFamily: 'Montserrat, sans-serif', sparkline: { enabled: false } },
              colors: ['#2ba7ca'],
              dataLabels: { enabled: false },
              stroke: { curve: 'smooth', width: 2 },
              fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } },
              xaxis: {
                categories: (analyticsData?.hourlyPurchaseRate ?? []).map(h => h.label),
                labels: {
                  rotate: -45,
                  style: { fontSize: '10px', colors: '#939598' },
                },
                axisBorder: { show: false },
                axisTicks: { show: false },
                tickAmount: 12,
              },
              yaxis: {
                labels: { style: { fontSize: '11px', colors: '#939598' } },
                tickAmount: 4,
              },
              grid: { borderColor: '#ebedf1', strokeDashArray: 3 },
              tooltip: { y: { formatter: (val: number) => `${val} registrations` } },
            }}
            height={240}
          />
        )}
      </div>

    </div>
  )
}

export default AnalyticsTab
